import { createFileRoute, Link } from "@tanstack/react-router";
import { m } from "framer-motion";
import { ArrowRight } from "lucide-react";
import {
  canonicalLinks,
  ogUrl,
  ogImage,
  jsonLd,
  breadcrumbSchema,
  webPageSchema,
} from "@/lib/seo";
import { PhotoBand } from "@/components/site/PhotoBand";
import scorecardImg from "@/assets/photos/operating-scorecard.webp";
import { NextStep } from "@/components/site/NextStep";
import { expertiseAreas } from "@/data/expertise";

const PATH = "/specialisations";
const TITLE = "Fractional COO Expertise for Business Operations";
const DESCRIPTION =
  "Six areas of expertise inside Ajility engagements: pricing and revenue, planning and forecasting, roles and incentives, project control, procurement, systems.";

export const Route = createFileRoute("/specialisations/")({
  head: () => ({
    meta: [
      { title: `${TITLE} | Ajility` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: `${TITLE} | Ajility` },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: `${TITLE} | Ajility` },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage("specialisations", "Ajility areas of expertise"),
      ogUrl(PATH),
    ],
    links: canonicalLinks(PATH),
    scripts: [
      jsonLd(
        webPageSchema({ image: "specialisations",
          path: PATH,
          name: `${TITLE} | Ajility`,
          description: DESCRIPTION,
          type: "CollectionPage",
        }),
      ),
      jsonLd(breadcrumbSchema([{ name: "Expertise", path: PATH }])),
    ],
  }),
  component: ExpertiseIndex,
});

function ExpertiseIndex() {
  return (
    <>
      <section className="relative overflow-hidden bg-midnight-deep pt-40 pb-20 text-frost">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <m.div
          initial={{ y: 20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative mx-auto max-w-7xl px-5 sm:px-8"
        >
          <span className="eyebrow block on-dark">Expertise</span>
          <h1 className="mt-5 font-serif text-4xl leading-[1.05] sm:text-5xl lg:text-6xl text-balance">
             Business operations expertise for stronger decisions
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-frost/75 text-pretty">
             Choose the commercial or operating problem you can see in the business. Ajility brings the deeper technical work through the priced services on the packages page.
          </p>
        </m.div>
      </section>

      <PhotoBand
        src={scorecardImg}
        alt="A printed operating scorecard beside a whiteboard tracking grid with markers on the tray"
        priority
      />

      <section className="bg-background py-24">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <ul className="grid gap-6">
            {expertiseAreas.map((item, i) => (
              <m.li
                key={item.to}
                initial={{ y: 16 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="card-panel card-interactive"
              >
                <h2 className="font-serif text-2xl text-foreground">
                  <Link to={item.to} className="transition hover:text-midnight">
                    {item.name}
                  </Link>
                </h2>
                <p className="mt-3 text-charcoal">{item.summary}</p>
                <Link
                  to={item.to}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-midnight"
                >
                  Read more about {item.name.toLowerCase()}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </m.li>
            ))}
          </ul>
        </div>
      </section>

      <NextStep
        heading="Use the Operating Scan to find the weakest areas"
        body="Answer 21 plain statements from Not us to Always. In about six minutes you see a provisional score naming the weakest areas, then choose whether you want a conversation."
        primaryLabel="Take the free Operating Scan"
        primaryTo="/scan"
        secondaryLabel="See packages and prices"
        secondaryTo="/packages"
      />
    </>
  );
}
